import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { downloadReceipt } from "@/lib/receipt";
import { Download, Loader2, AlertTriangle } from "lucide-react";

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/wallet/transactions?limit=200").then(({ data }) => {
      setTxs(data.transactions || []);
      setLoading(false);
    });
  }, []);

  const handleReceipt = async (txId) => {
    setDownloading(txId);
    await downloadReceipt(txId);
    setDownloading(null);
  };

  return (
    <AppLayout>
      <div className="text-eyebrow text-white/55">Full history</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 text-white">
        Transactions
      </h1>
      <p className="text-white/60 mt-2 text-sm">
        A receipt is auto-generated for every completed transaction. Download
        or re-download any of them below.
      </p>

      <div className="mt-8 fv-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/60">Loading…</div>
        ) : txs.length === 0 ? (
          <div className="p-10 text-center text-white/60">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead className="text-left bg-white/[0.04] text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 md:px-6 py-3">When</th>
                  <th className="px-4 md:px-6 py-3">Type</th>
                  <th className="px-4 md:px-6 py-3">Description</th>
                  <th className="px-4 md:px-6 py-3">Mode</th>
                  <th className="px-4 md:px-6 py-3">Status</th>
                  <th className="px-4 md:px-6 py-3 text-right">Amount</th>
                  <th className="px-4 md:px-6 py-3 text-right">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {txs.map((t) => {
                  const sign =
                    t.type === "deposit" || t.type === "transfer_in" || t.type === "promo_bonus"
                      ? "+"
                      : "−";
                  const color = sign === "+" ? "text-[#7BBF8E]" : "text-[#E27A6A]";
                  const canDownload = t.status === "completed" || t.status === "pending_approval";
                  return (
                    <tr key={t.id} className="hover:bg-white/[0.03]">
                      <td className="px-4 md:px-6 py-3 whitespace-nowrap text-white/80">
                        {new Date(t.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 md:px-6 py-3 capitalize text-white/85">
                        <div className="flex items-center gap-2">
                          <span>{(t.type || "").replace("_", " ")}</span>
                          {t.fraud_flag ? (
                            <span
                              title={t.fraud_reason === "large_amount" ? "Flagged — large amount" : "Flagged — unusual for this account"}
                              data-testid={`fraud-badge-${t.id}`}
                              className="fv-badge-danger inline-flex items-center gap-1 !py-0 !px-1.5 !text-[9px] font-bold uppercase tracking-wider"
                            >
                              <AlertTriangle className="w-2.5 h-2.5" /> Flagged
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 text-white/75 max-w-[300px] truncate">
                        {t.description}
                      </td>
                      <td className="px-4 md:px-6 py-3 capitalize text-white/70">{t.mode}</td>
                      <td className="px-4 md:px-6 py-3 capitalize text-white/70">
                        {(t.status || "").replace("_", " ")}
                      </td>
                      <td className={`px-4 md:px-6 py-3 text-right font-mono ${color}`}>
                        {sign}${(t.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 md:px-6 py-3 text-right">
                        {canDownload ? (
                          <button
                            onClick={() => handleReceipt(t.id)}
                            disabled={downloading === t.id}
                            data-testid={`receipt-download-${t.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-[#B58957] hover:bg-white/[0.06] disabled:opacity-60"
                          >
                            {downloading === t.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            PDF
                          </button>
                        ) : (
                          <span className="text-xs text-white/30">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
